import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSocket } from "../../contexts/SocketContext";
import { useAccess } from "../../../features/access/context/AccessProvider";
import { PERMISSIONS } from "../../../features/access/constants/permissionKeys";
import { fetchAccountsQueue } from "../models/paymentsV2Model";
import { playChime, showNotification } from "../../misc_functions";
import notificationSound from "../../../assets/sounds/wati_notification.wav";
import uzdLogo from "../../../assets/images/uzd_logo_small.jpeg";

const AccountsNotificationsContext = createContext(null);
const EMPTY_CONTEXT = {
  enabled: false,
  pendingCount: 0,
  queueData: {},
  queueRevision: 0,
  lastResolved: null,
  navigationTarget: null,
  refreshQueue: queue => fetchAccountsQueue(queue || "all"),
  setActiveQueue: () => {},
};

const eventData = payload => payload?.data ?? payload;
const rowIdentity = row => `${row?.entity_type}:${row?.entity_id}`;

export function AccountsNotificationsProvider({ children }) {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const { socket, isConnected } = useSocket();
  const { can, status: accessStatus, revision: accessRevision, refreshAccess } = useAccess();
  const enabled = authState?.isAuthenticated
    && accessStatus === "ready"
    && can(PERMISSIONS.PAYMENTS_VIEW_ACCOUNTS_WORKSPACE);
  const [pendingCount, setPendingCount] = useState(0);
  const [queueData, setQueueData] = useState({});
  const [queueRevision, setQueueRevision] = useState(0);
  const [lastResolved, setLastResolved] = useState(null);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const activeQueueRef = useRef("all");
  const inFlightRef = useRef(new Map());
  const refreshTimerRef = useRef(null);
  const queuedRefreshesRef = useRef(new Set());
  const seenEventIdsRef = useRef(new Set());
  const browserNotificationsRef = useRef(new Map());

  const refreshQueue = useCallback(async (queue = "all") => {
    if (!enabled) return null;
    const normalizedQueue = queue || "all";
    if (inFlightRef.current.has(normalizedQueue)) return inFlightRef.current.get(normalizedQueue);
    const request = fetchAccountsQueue(normalizedQueue)
      .then(data => {
        setQueueData(current => ({ ...current, [normalizedQueue]: data }));
        if (normalizedQueue === "all") {
          setPendingCount(Number(data?.summary?.total || 0));
          const activeEventIds = new Set((data?.rows || []).map(row => `payments-v2:${row.queue}:${row.entity_type}:${row.entity_id}`));
          seenEventIdsRef.current.forEach(eventId => {
            if (activeEventIds.has(eventId)) return;
            notification.destroy(eventId);
            browserNotificationsRef.current.get(eventId)?.close?.();
            browserNotificationsRef.current.delete(eventId);
            seenEventIdsRef.current.delete(eventId);
          });
        }
        setQueueRevision(current => current + 1);
        return data;
      })
      .catch(error => {
        if (error?.response?.status === 403) refreshAccess({ background: true }).catch(() => {});
        throw error;
      })
      .finally(() => inFlightRef.current.delete(normalizedQueue));
    inFlightRef.current.set(normalizedQueue, request);
    return request;
  }, [enabled, notification, refreshAccess]);

  const scheduleRefresh = useCallback((...queues) => {
    if (!enabled) return;
    queues.filter(Boolean).forEach(queue => queuedRefreshesRef.current.add(queue));
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      const pendingQueues = [...queuedRefreshesRef.current];
      queuedRefreshesRef.current.clear();
      Promise.allSettled(pendingQueues.map(refreshQueue));
    }, 180);
  }, [enabled, refreshQueue]);

  const navigateToWork = useCallback(data => {
    setNavigationTarget({
      queue: data.queue || "all",
      entity_type: String(data.entity_type || ""),
      entity_id: String(data.entity_id ?? ""),
      event_id: data.event_id,
    });
    const params = new URLSearchParams({
      queue: data.queue || "all",
      entity_type: String(data.entity_type || ""),
      entity_id: String(data.entity_id ?? ""),
    });
    navigate(`/payments/accounts?${params.toString()}`);
  }, [navigate]);

  useEffect(() => {
    if (!enabled || !socket) return undefined;

    const onRequired = payload => {
      const data = eventData(payload);
      if (!data?.event_id || seenEventIdsRef.current.has(data.event_id)) return;
      seenEventIdsRef.current.add(data.event_id);
      const openWork = () => navigateToWork(data);

      notification.warning({
        key: data.event_id,
        message: data.title || "Accounts action required",
        description: data.message || payload?.message || "New work is waiting in Accounts.",
        duration: 0,
        placement: "bottomRight",
        onClick: openWork,
        style: { cursor: "pointer" },
      });
      const browserNotification = showNotification(
        data.title || "Accounts action required",
        data.message || payload?.message,
        uzdLogo,
        { tag: data.event_id, onClick: openWork },
      );
      if (browserNotification) browserNotificationsRef.current.set(data.event_id, browserNotification);
      playChime(notificationSound);
      scheduleRefresh(data.queue, "all");
    };

    const onResolved = payload => {
      const data = eventData(payload);
      if (!data?.event_id) return;
      notification.destroy(data.event_id);
      browserNotificationsRef.current.get(data.event_id)?.close?.();
      browserNotificationsRef.current.delete(data.event_id);
      seenEventIdsRef.current.delete(data.event_id);
      setLastResolved({ ...data, identity: rowIdentity(data), receivedAt: Date.now() });
      scheduleRefresh(data.queue, "all");
    };

    const onPaymentsUpdated = () => scheduleRefresh(activeQueueRef.current, "all");
    socket.on("accounts:action_required", onRequired);
    socket.on("accounts:action_resolved", onResolved);
    socket.on("payments:v2_updated", onPaymentsUpdated);
    return () => {
      socket.off("accounts:action_required", onRequired);
      socket.off("accounts:action_resolved", onResolved);
      socket.off("payments:v2_updated", onPaymentsUpdated);
    };
  }, [enabled, socket, notification, navigateToWork, scheduleRefresh]);

  // The all-work queue is durable notification state. Rebuild it after login,
  // reconnect, and access changes instead of relying on events missed offline.
  useEffect(() => {
    if (enabled && isConnected) scheduleRefresh("all", activeQueueRef.current);
  }, [enabled, isConnected, accessRevision, scheduleRefresh]);

  useEffect(() => {
    if (enabled) return undefined;
    clearTimeout(refreshTimerRef.current);
    queuedRefreshesRef.current.clear();
    seenEventIdsRef.current.forEach(eventId => notification.destroy(eventId));
    seenEventIdsRef.current.clear();
    browserNotificationsRef.current.forEach(item => item?.close?.());
    browserNotificationsRef.current.clear();
    setPendingCount(0);
    setQueueData({});
    setLastResolved(null);
    setNavigationTarget(null);
    return undefined;
  }, [enabled, notification]);

  useEffect(() => () => {
    clearTimeout(refreshTimerRef.current);
    seenEventIdsRef.current.forEach(eventId => notification.destroy(eventId));
    browserNotificationsRef.current.forEach(item => item?.close?.());
  }, [notification]);

  const setActiveQueue = useCallback(queue => { activeQueueRef.current = queue || "all"; }, []);
  const value = useMemo(() => ({
    enabled, pendingCount, queueData, queueRevision, lastResolved, navigationTarget, refreshQueue, setActiveQueue,
  }), [enabled, pendingCount, queueData, queueRevision, lastResolved, navigationTarget, refreshQueue, setActiveQueue]);

  return <AccountsNotificationsContext.Provider value={value}>{children}</AccountsNotificationsContext.Provider>;
}

export const useAccountsNotifications = () => useContext(AccountsNotificationsContext) || EMPTY_CONTEXT;
export { AccountsNotificationsContext };
